import { Link } from "react-router-dom";
import { Button } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import type { ScrollGlobeAction } from "./scrollGlobe.types";

export interface ScrollGlobeActionsProps {
  actions: readonly ScrollGlobeAction[];
  align?: "left" | "center" | "right";
}

/**
 * La fila de botones de una sección.
 *
 * ── Usa el `Button` del sistema, no markup suelto ────────────────────
 * El original escribe las clases de la píldora a mano en cada acción.
 * `Button` ya trae esa misma forma —`primary` es la píldora violeta con
 * sombra; `ghost` es la de borde sobre fondo translúcido— y encima trae
 * lo que el markup a mano no tenía: anillo de foco visible, estado
 * deshabilitado que también funciona sobre un enlace, y `motion-reduce`
 * apagando el escalado del hover.
 *
 * ── Enlace o botón, según lo que haga ────────────────────────────────
 * Con `to` se pinta un `<Link>` de verdad; con `onClick`, un `<button>`.
 * El tipo `ScrollGlobeAction` obliga a elegir uno de los dos, así que no
 * hay forma de escribir por accidente un botón que en realidad navega.
 */
export function ScrollGlobeActions({ actions, align = "left" }: ScrollGlobeActionsProps) {
  return (
    <div
      className={cn(
        "flex flex-col flex-wrap gap-3 sm:flex-row sm:gap-4",
        align === "center" && "justify-center",
        align === "right" && "justify-end",
      )}
    >
      {actions.map((action) => {
        const variant = action.variant === "primary" ? "primary" : "ghost";

        return action.to !== undefined ? (
          <Button key={action.label} as={Link} to={action.to} variant={variant} size="lg">
            {action.label}
          </Button>
        ) : (
          <Button key={action.label} onClick={action.onClick} variant={variant} size="lg">
            {action.label}
          </Button>
        );
      })}
    </div>
  );
}
